import React from 'react'
import { Box, Typography } from '@mui/material'
import { AmchartSeeker } from '@ui-seeker/core'

import { analyzerChartVisibility, isAnalyzerLoading } from 'Pages/AnalyzersV2/@signals'
import { POSITION_MAIN_COLOR } from 'Pages/AnalyzersV2/@constants'
import ChartFullScreen from 'Pages/AnalyzersV2/@components/ChartFullScreen'
import {
	buildRankData,
	findMainDataKey,
	getMaxRank,
	priceField
} from 'Pages/AnalyzersV2/@components/RankEvolutionChart/@utils'
import { AppCardTitle, AppResultsSkeleton } from '@components'
import { dsColorNew } from '@palette'

const TITLE = 'Rank evolution'
const ICON = 'ranking-star'


export default function RankEvolutionChart({ chartData, keySeries }) {
	if (isAnalyzerLoading.value) return <AppResultsSkeleton chart height={500}/>
	if (!chartData?.length || !keySeries?.length) return <></>

	const dataKeys = keySeries.map(({ dataKey }) => dataKey)
	const data = buildRankData(chartData, dataKeys)
	const maxRank = getMaxRank(data, dataKeys)
	const mainKey = findMainDataKey(chartData, dataKeys)
	const subtitle = `Market position by pickup date · ${mainKey} highlighted`

	return <Box>
		<ChartFullScreen rootID="RankEvolutionChart" title={TITLE} subtitle={subtitle} icon={ICON} renderChart={viewProps => <AmchartSeeker
		title={<AppCardTitle icon={ICON} title={TITLE} subtitle={subtitle}/>}
		{...viewProps}
		data={data}
		series={keySeries.map(({ dataKey, label, color }, index) => {
			const isMain = dataKey === mainKey

			return {
				name: label || dataKey,
				yField: dataKey,
				xField: 'date',
				// The main brand takes the app blue so it reads as "you" on the line
				// and on its tooltip.
				color: isMain ? POSITION_MAIN_COLOR : color,
				// Gaps must break the line: a missing rank means no rate that day.
				connect: false,
				tension: 1,
				hidden: !analyzerChartVisibility.value.includes(dataKey),
				strokes: { strokeWidth: isMain ? 4 : 2 },
				tooltip: {
					pointerOrientation: 'horizontal',
					labelText: `{name}: rank {valueY.formatNumber("#")} · {${priceField(index)}}`,
				},
			}
		})}
		chartProps={{
			// One label, the brand under the pointer: with a tooltip per brand the seventeen labels of a
			// date piled up and shuffled as the pointer moved (Curro, 2026-10-20)
			chart: { maxTooltipDistance: -1 },
			showBullets: true,
			enabledPDF: false,
			// Prices in the tooltip keep two decimals; the rank is formatted as an
			// integer inline in `labelText`.
			numberFormat: "#,###.00",
			dateFormatReceived: 'yyyyMMdd',
			// Horizontal is on unless hidden; vertical needs opting in.
			scrollbarX: { hidden: false },
			scrollbarY: { show: true },
			xAxis: {
				categoryField: 'date',
				maxDeviation: 0.3,
				isDateAxis: true,
				dateFormat: 'dd MMM yyyy (EEE)',
				baseInterval: { timeUnit: 'day', count: 1 },
			},
			yAxis: {
				title: 'Market rank (1 = cheapest)',
				min: .5,
				max: maxRank + .5,
				maxPrecision: 0,
				// 1 on top, so "better position" reads as "higher on the chart".
				renderer: { inversed: true },
			},
		}}
	/>}/>

		<Typography sx={{ display: 'block', px: 1, mt: 1, fontSize: 12, color: dsColorNew.textSubtle }}>
			Vertical axis = market rank (1 = cheapest). A break in a line means no
			rate that day, not a missing value.
		</Typography>
	</Box>
}
